import React from "react";
import {
  toWSDateString,
  WSEmptyState,
  WSInfiniteScroll,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { useGoToMyResourceDetails } from "../paths";
import { useQueryInsuranceCoverages } from "../../../query/insuranceCoverage/queries/useQueryInsuranceCoverages";
import flatten from "lodash/flatten";
import { IInsuranceCoverage } from "../../../services/api/insurance/coverage/types";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { selectorPolicyNameByPolicyType } from "../selectors/selectorPolicyNameByPolicyType";
import { selectorPolicyTypeByPolicyType } from "../selectors/selectorPolicyTypeByPolicyType";
import { useQueryPlanEnrollment } from "../../../query/benefits/useQueryPlanEnrollment";
import { WSQueries } from "../../../query/WSQuery";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import { useGetNow } from "../../../shared/hooks/useGetNow";

const PolicyNameCell = ({
  insuranceCoverage
}: {
  insuranceCoverage: IInsuranceCoverage;
}) => {
  const qEnrollmentPlan = useQueryPlanEnrollment(insuranceCoverage.sourceId);

  return (
    <WSTableCell
      text={
        insuranceCoverage.title
          ? insuranceCoverage.title
          : qEnrollmentPlan.data?.planType
          ? selectorPolicyNameByPolicyType(
              insuranceCoverage.policyType,
              qEnrollmentPlan.data?.planType
            )
          : undefined
      }
      secondaryText={`Updated ${toWSDateString(
        insuranceCoverage.events.createdAt,
        "input"
      )}`}
    />
  );
};

export const IndependentContractorInsuranceList: React.FC<{}> = () => {
  const goToMyResourceDetails = useGoToMyResourceDetails();

  const qInsuranceCoverages = useQueryInsuranceCoverages();
  const now = useGetNow();

  const columns: Array<WSTableColumn<IInsuranceCoverage>> = [
    {
      config: {
        header: {
          text: "Policy name"
        }
      },
      renderFunction: row => <PolicyNameCell insuranceCoverage={row.data} />
    },
    {
      config: {
        header: {
          text: "Type"
        }
      },
      renderFunction: row => (
        <WSTableCell
          text={selectorPolicyTypeByPolicyType(row.data.policyType)}
          secondaryText={
            row.data.descriptionOfOperations
              ? row.data.descriptionOfOperations
              : undefined
          }
        />
      )
    },
    // {
    //   config: {
    //     header: {
    //       text: "Client requirements"
    //     }
    //   },
    //   renderFunction: row => <WSTableCell text={String("TODO")} />
    // },
    {
      config: {
        header: {
          text: "Status"
        }
      },
      renderFunction: row => (
        <WSTableCell
          pill={selectorInsuranceCoverageStatusPillByExpirationDate(
            row.data.expirationDate
          )}
          secondaryText={selectorInsuranceCoverageStatusDescriptionByInsurance(
            row.data,
            now
          )}
        />
      )
    }
  ];

  const tableData = flatten(qInsuranceCoverages.data?.pages)?.map(item => ({
    data: item,
    id: item.insuranceCoverageId
  }));

  return (
    <WSQueries queries={{ qInsuranceCoverages }}>
      {() => (
        <WSInfiniteScroll
          onLoad={() => qInsuranceCoverages.fetchNextPage()}
          loadMore={tableData.length > 0}
          endOfList={!qInsuranceCoverages.hasNextPage}
          loading={!!qInsuranceCoverages.isFetchingNextPage}
        >
          {tableData.length > 0 ? (
            <WSTable<IInsuranceCoverage>
              columns={columns}
              tableData={tableData}
              onRowClick={row => {
                goToMyResourceDetails(row.id);
              }}
            />
          ) : (
            <WSEmptyState
              theme="neutral"
              type="documents"
              title="No documents yet"
              size="S"
              orientation="horizontal"
            />
          )}
        </WSInfiniteScroll>
      )}
    </WSQueries>
  );
};
